import { call, put } from 'redux-saga/effects';
import {
  deviceProcessingStarted,
  deviceProcessingStopped,
} from 'features/streaming/streamingSlice';
import { DefaultDevice, getDefaultMediaDevice } from 'features/user-media/utils';
import { PublishingFeedStatus, RTCClient } from 'utils/webrtc';
import { devicesAutoChanged, mediaDevicesUpdated } from 'features/user-media/userMediaSlice';
import { EnumeratedMediaDevices } from 'features/user-media/types';
import { AutoChangedDevices } from 'features/user-media/utils/findDeviceDifferences';
import * as Sentry from '@sentry/react';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { logger } from 'utils/logger';

export function* handleRemovedDevicesSaga(
  updatedDevices: EnumeratedMediaDevices,
  removedActiveDevices: AutoChangedDevices
) {
  yield put(
    mediaDevicesUpdated(updatedDevices, {
      updatePermissions: true,
    })
  );

  if (removedActiveDevices.audioinput) {
    yield put(deviceProcessingStarted('audio'));

    try {
      const feedConnected = RTCClient.publishingFeed.status === PublishingFeedStatus.connected;

      if (feedConnected) {
        const canBroadcastAudio: boolean = yield call(
          hasPermissionsSaga,
          PermissionTypes.broadcastAudio
        );

        if (canBroadcastAudio) {
          const deviceId: DefaultDevice = yield call(
            getDefaultMediaDevice,
            updatedDevices,
            'audioinput'
          );

          if (!deviceId) {
            logger.remote().info('No replacement audio device found', {
              from: removedActiveDevices.audioinput?.label,
            });
            yield call(RTCClient.publishingFeed.blockAudioPublishing);
          } else {
            const isBroadcasting = RTCClient.publishingFeed.mediaStates.audio.enabled;

            logger
              .remote({ capture: 'media-devices' })
              .debug('Updated active audio device in Redux', {
                from: removedActiveDevices.audioinput?.label,
                to: deviceId,
                broadcasting: isBroadcasting,
              });

            yield put(
              devicesAutoChanged({
                kind: 'audioinput',
                id: deviceId,
              })
            );

            if (isBroadcasting) {
              yield call(RTCClient.publishingFeed.stopAudioBroadcasting, true);
            }
          }
        } else {
          logger.remote().info('Audio blocked - no broadcast permission');
          yield call(RTCClient.publishingFeed.blockAudioPublishing);
        }
      }
    } catch (error) {
      logger.remote().error('Error handling removed audio device', error);
      Sentry.captureException(error);
      yield call(RTCClient.publishingFeed.blockAudioPublishing);
    }

    yield put(deviceProcessingStopped('audio'));
  }

  if (removedActiveDevices.videoinput) {
    yield put(deviceProcessingStarted('video'));

    try {
      const feedConnected = RTCClient.publishingFeed.status === PublishingFeedStatus.connected;

      if (feedConnected) {
        const canBroadcastVideo: boolean = yield call(
          hasPermissionsSaga,
          PermissionTypes.broadcastVideo
        );

        if (canBroadcastVideo) {
          const deviceId: DefaultDevice = yield call(
            getDefaultMediaDevice,
            updatedDevices,
            'videoinput'
          );

          if (!deviceId) {
            logger.remote().info('No replacement video device found', {
              from: removedActiveDevices.videoinput?.label,
            });
            yield call(RTCClient.publishingFeed.blockVideoPublishing);
          } else {
            const isBroadcasting = RTCClient.publishingFeed.mediaStates.video.enabled;

            logger
              .remote({ capture: 'media-devices' })
              .debug('Updated active video device in Redux', {
                from: removedActiveDevices.videoinput?.label,
                to: deviceId,
                broadcasting: isBroadcasting,
              });

            yield put(
              devicesAutoChanged({
                kind: 'videoinput',
                id: deviceId,
              })
            );

            if (isBroadcasting) {
              yield call(RTCClient.publishingFeed.stopVideoBroadcasting);
            }
          }
        } else {
          logger.remote().info('Video blocked - no broadcast permission');
          yield call(RTCClient.publishingFeed.blockVideoPublishing);
        }
      }
    } catch (error) {
      logger.remote().error('Error handling removed video device', error);
      Sentry.captureException(error);
      yield call(RTCClient.publishingFeed.blockVideoPublishing);
    }

    yield put(deviceProcessingStopped('video'));
  }

  if (removedActiveDevices.audiooutput) {
    const deviceId: DefaultDevice = yield call(
      getDefaultMediaDevice,
      updatedDevices,
      'audiooutput'
    );

    if (!deviceId) {
      logger.remote().info('No replacement audio output device found', {
        from: removedActiveDevices.audiooutput?.label,
      });
    } else {
      yield put(
        devicesAutoChanged({
          kind: 'audiooutput',
          id: deviceId,
        })
      );
    }
  }
}
